import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  LayoutDashboard,
  Users,
  Building2,
  LogOut,
  Shield,
  FileText,
  FolderKanban,
  DollarSign,
  ClipboardList,
  Search,
  MessageSquare,
  MessageSquareWarning,
  Headset,
  BarChart3,
  Home,
  TrendingUp,
  BarChart2,
  CalendarDays,
  Rocket,
  UserCheck,
  Gift,
  AlertTriangle,
  Hammer,
  PhoneIncoming,
  Scale,
  Receipt,
  Timer,
  ChevronDown,
  ChevronRight,
  CreditCard,
  Bell,
  RotateCcw,
  Filter,
  ShieldAlert,
  MapPin,
  Star,
  FileWarning,
  Radio,
  PhoneOutgoing,
  UserX,
  Tag,
  Flag,
  Copy,
  ZapOff,
  HandCoins,
  Layers,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { trpc } from "@/lib/trpc";
import { useState, useRef, useEffect } from "react";
import { filterNavItems, isReadOnly, NavItem, PageKey } from "@/lib/rbac";
import { useOpsNotifications } from "@/hooks/useOpsNotifications";
import { CommandPalette } from "@/components/CommandPalette";
import { RouteErrorBoundary } from "@/components/layout/RouteErrorBoundary";

interface NavGroup {
  key: string;
  label: string;
  items: NavItem[];
}

export function AdminLayout({ children }: { children: React.ReactNode }) {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState("");
  const [collapsedGroups, setCollapsedGroups] = useState<Record<string, boolean>>({});
  const [notifOpen, setNotifOpen] = useState(false);
  const notifRef = useRef<HTMLDivElement>(null);
  const { notifications, unreadCount, markAllRead } = useOpsNotifications();

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (notifRef.current && !notifRef.current.contains(e.target as Node)) {
        setNotifOpen(false);
      }
    }
    if (notifOpen) document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [notifOpen]);

  const toggleGroup = (key: string) => {
    setCollapsedGroups((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  // Badges pour notifications
  const { data: goQueueCount } = trpc.admin.dashboard.goQueue.useQuery({ limit: 100 });
  const goQueueBadge = goQueueCount?.length || 0;

  // Unread messages count
  const { data: unreadMessagesData } = trpc.messaging.getUnreadCount.useQuery();
  const unreadMsgBadge = unreadMessagesData?.count || 0;

  // Open support tickets count
  const { data: supportStats } = trpc.admin.support.stats.useQuery(undefined, {
    retry: false,
  });
  const supportBadge = supportStats?.totalOpen || 0;

  // Pending artisan verifications badge
  const { data: pendingArtisansData } = trpc.admin.travauxbtp.listPendingArtisans.useQuery(
    { page: 1, limit: 1 },
    { retry: false }
  );
  const artisansBadge = pendingArtisansData?.total || 0;

  // Urgent SLA leads badge (<12h remaining)
  const { data: slaLeadsData } = trpc.admin.leadSla.getLeadSLAStatus.useQuery(
    { page: 1, limit: 100 },
    { retry: false, refetchInterval: 60_000 }
  );
  const slaUrgentBadge = (slaLeadsData?.items ?? []).filter((item) => {
    if (!item.refundEligibleAt) return false;
    const ms = new Date(item.refundEligibleAt).getTime() - Date.now();
    return ms > 0 && ms < 12 * 60 * 60 * 1000;
  }).length;

  // Urgent trial alerts badge (CRITICAL J-3 + EXPIRED J-0)
  // Source: getTrialAlerts (admin.companies.list n'expose pas planExpiresAt).
  const { data: trialAlertsData } = trpc.admin.trialAlerts.getTrialAlerts.useQuery(
    undefined,
    { retry: false, refetchInterval: 120_000 }
  );
  const trialAlertsBadge = new Set(
    (trialAlertsData ?? [])
      .filter((a: any) => a.severity === "CRITICAL" || a.severity === "EXPIRED")
      .map((a: any) => a.companyId)
  ).size;

  // Active fraud alerts badge
  const { data: fraudAlertsData } = trpc.admin.fraudAlerts.listActive.useQuery(undefined, {
    retry: false,
    refetchInterval: 60_000,
  });
  const fraudAlertsBadge = fraudAlertsData?.length || 0;

  // Sidebar OPS — organisée par WORKFLOW (pas par produit), par fréquence d'usage.
  // Mega-groupe "TravauxBTP" (26 items mélangés) découpé en 5 sous-groupes thématiques :
  // Dashboard | Particuliers | Artisans | Opérations marketplace | Finance marketplace |
  // Analytics marketplace | SaaS B2B | Support | Administration.
  const navGroups: NavGroup[] = [
    // ═══════════ HOME ═══════════
    {
      key: "dashboard",
      label: "Accueil",
      items: [
        { path: "/", label: "Dashboard principal", icon: LayoutDashboard, pageKey: "DASHBOARD" },
        { path: "/search", label: "Recherche globale", icon: Search },
      ],
    },

    // ═══════════ MARKETPLACE — DEMANDE (PARTICULIERS) ═══════════
    {
      key: "particuliers",
      label: "Particuliers",
      items: [
        { path: "/private-projects", label: "Projets particuliers", icon: Home, pageKey: "PRIVATE_PROJECTS" },
        { path: "/ops-call-queue", label: "Call queue", icon: PhoneOutgoing, pageKey: "PRIVATE_PROJECTS" },
        { path: "/lp-leads", label: "LP Leads", icon: PhoneIncoming, pageKey: "LP_LEADS" },
        { path: "/particulier-quality", label: "Qualité particuliers", icon: Star, pageKey: "PARTICULIER_QUALITY" },
      ],
    },

    // ═══════════ MARKETPLACE — OFFRE (ARTISANS) ═══════════
    // Note : modération artisan-level uniquement. Modération de contenu (avis/messages)
    // est dans le groupe "Opérations" pour cohérence.
    {
      key: "artisans",
      label: "Artisans",
      items: [
        { path: "/travauxbtp/artisans", label: "Modération inscriptions", icon: UserCheck, badge: artisansBadge, pageKey: "ARTISANS_MODERATION" },
        { path: "/travauxbtp/artisans/all", label: "Annuaire artisans", icon: Users, pageKey: "ARTISANS_MODERATION" },
        { path: "/travauxbtp/documents", label: "Documents expiration", icon: FileWarning, pageKey: "DOCUMENTS_EXPIRATION" },
        { path: "/ops-inactive", label: "Artisans inactifs", icon: ZapOff, pageKey: "ARTISANS_MODERATION" },
        { path: "/ops-duplicates", label: "Comptes suspects", icon: Copy, pageKey: "ARTISANS_MODERATION" },
      ],
    },

    // ═══════════ MARKETPLACE — OPÉRATIONS LIVE ═══════════
    // Toutes les modérations contenu + workflows live regroupés.
    {
      key: "ops-live",
      label: "Opérations",
      items: [
        { path: "/sla-leads", label: "SLA Leads", icon: Timer, badge: slaUrgentBadge, pageKey: "SLA_LEADS" },
        { path: "/ops-no-shows", label: "No-shows artisans", icon: UserX, pageKey: "PRIVATE_PROJECTS" },
        { path: "/ops-quick-reports", label: "Signalements", icon: Flag, pageKey: "PRIVATE_PROJECTS" },
        { path: "/disputes", label: "Litiges", icon: Scale, pageKey: "DISPUTES" },
        { path: "/travauxbtp/reviews", label: "Modération avis", icon: Shield, pageKey: "REVIEWS_MODERATION" },
        { path: "/messagerie-moderation", label: "Modération messages", icon: MessageSquareWarning, pageKey: "MESSAGE_MODERATION" },
      ],
    },

    // ═══════════ MARKETPLACE — FINANCE ═══════════
    {
      key: "marketplace-finance",
      label: "Finance marketplace",
      items: [
        { path: "/transactions", label: "Transactions crédits", icon: Receipt, pageKey: "TRANSACTIONS" },
        { path: "/refunds", label: "Remboursements", icon: RotateCcw, pageKey: "REFUNDS" },
        { path: "/ops-bulk-gift", label: "Gift crédits (bulk)", icon: HandCoins, pageKey: "ARTISANS_MODERATION" },
      ],
    },

    // ═══════════ MARKETPLACE — MARKETING / GROWTH ═══════════
    // Programme parrainage + acquisition funnel (actions marketing).
    {
      key: "marketplace-marketing",
      label: "Marketing",
      items: [
        { path: "/travauxbtp/referrals", label: "Parrainages", icon: Gift, pageKey: "TRAVAUXBTP_REFERRALS" },
        { path: "/acquisition-funnel", label: "Funnel acquisition", icon: Filter, pageKey: "ACQUISITION_FUNNEL" },
        { path: "/broadcast", label: "Broadcast", icon: Radio, pageKey: "BROADCAST" },
      ],
    },

    // ═══════════ MARKETPLACE — ANALYTICS / PILOTAGE ═══════════
    {
      key: "marketplace-analytics",
      label: "Analytics marketplace",
      items: [
        { path: "/travauxbtp", label: "Dashboard TravauxBTP", icon: Hammer, pageKey: "TRAVAUXBTP_DASHBOARD" },
        { path: "/marketplace-stats", label: "Stats Marketplace", icon: BarChart3, pageKey: "MARKETPLACE_STATS" },
        { path: "/marketplace-geo", label: "Densité géographique", icon: MapPin, pageKey: "MARKETPLACE_GEO" },
      ],
    },

    // ═══════════ SAAS B2B (autre business unit) ═══════════
    {
      key: "saas-companies",
      label: "Entreprises SaaS",
      items: [
        { path: "/companies", label: "Toutes les entreprises", icon: Building2, pageKey: "COMPANIES" },
        { path: "/conversion-pipeline", label: "Pipeline conversion", icon: TrendingUp, pageKey: "CONVERSION_PIPELINE" },
        { path: "/onboarding", label: "Onboarding", icon: Rocket, pageKey: "ONBOARDING" },
        { path: "/churn-risk", label: "Churn Risk", icon: AlertTriangle, pageKey: "CHURN_RISK" },
        { path: "/subscriptions", label: "Abonnements", icon: CreditCard, pageKey: "SUBSCRIPTIONS" },
        { path: "/plans-saas", label: "Plans SaaS", icon: Layers, pageKey: "PLANS_SAAS" },
        { path: "/trial-alerts", label: "Alertes Trials", icon: Bell, badge: trialAlertsBadge, pageKey: "TRIAL_ALERTS" },
      ],
    },
    {
      key: "commercial",
      label: "Commercial SaaS",
      items: [
        { path: "/tenders", label: "Appels d'offres", icon: FileText, pageKey: "TENDERS" },
        { path: "/go-queue", label: "GO Queue", icon: ClipboardList, badge: goQueueBadge, pageKey: "GO_QUEUE" },
        { path: "/sales/pipeline", label: "Pipeline commercial", icon: TrendingUp, pageKey: "SALES_PIPELINE" },
        { path: "/sales/leads", label: "Leads", icon: Users, pageKey: "SALES_LEADS" },
        { path: "/sales/activities", label: "Activités", icon: CalendarDays, pageKey: "SALES_ACTIVITIES" },
        { path: "/sales/performance", label: "Performance", icon: BarChart2, pageKey: "SALES_PERFORMANCE" },
        { path: "/sales/forecast", label: "Prévisions", icon: BarChart3, pageKey: "SALES_FORECAST" },
        { path: "/partners", label: "Partenaires", icon: Users, pageKey: "PARTNERS" },
        { path: "/projects", label: "Projets clients SaaS", icon: FolderKanban, pageKey: "PROJECTS" },
      ],
    },

    // ═══════════ STAFF ═══════════
    {
      key: "support",
      label: "Support",
      items: [
        { path: "/messages", label: "Messagerie", icon: MessageSquare, badge: unreadMsgBadge, pageKey: "MESSAGES" },
        { path: "/support", label: "Tickets support", icon: Headset, badge: supportBadge, pageKey: "SUPPORT" },
      ],
    },
    // Tags artisans/particuliers vivent ici car la page OpsTags gère les 2 (hors scope Artisans).
    {
      key: "administration",
      label: "Administration",
      items: [
        { path: "/billing", label: "Facturation interne", icon: DollarSign, pageKey: "BILLING" },
        { path: "/fraud-alerts", label: "Alertes fraude", icon: ShieldAlert, badge: fraudAlertsBadge, pageKey: "FRAUD_ALERTS" },
        { path: "/ops-tags", label: "Tags VIP (artisans+particuliers)", icon: Tag, pageKey: "ARTISANS_MODERATION" },
        { path: "/audit-log", label: "Journal d'audit", icon: ClipboardList, pageKey: "AUDIT_LOG" },
      ],
    },
  ];

  // Filter each group by RBAC, then filter by search query
  const filteredGroups = navGroups
    .map((group) => {
      let items = filterNavItems(user?.staffRole || null, group.items);
      if (searchQuery.trim()) {
        const q = searchQuery.toLowerCase();
        items = items.filter((item) => item.label.toLowerCase().includes(q));
      }
      return { ...group, items };
    })
    .filter((group) => group.items.length > 0);

  return (
    <div className="min-h-screen bg-background">
      {/* Sidebar */}
      <div className="fixed inset-y-0 left-0 w-64 bg-sidebar border-r border-sidebar-border">
        <div className="flex h-full flex-col">
          {/* Header */}
          <div className="flex h-16 items-center gap-2 border-b border-sidebar-border px-4">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary">
              <Shield className="h-5 w-5 text-primary-foreground" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-sm">Constrigo Ops</div>
              <div className="text-xs text-muted-foreground">Centre opérationnel</div>
            </div>
            {isReadOnly(user?.staffRole) && (
              <Badge variant="secondary" className="text-[10px] px-1.5 py-0.5 shrink-0">
                Lecture seule
              </Badge>
            )}
            {/* Notification bell */}
            <div className="relative" ref={notifRef}>
              <button
                type="button"
                className="relative p-1.5 rounded-md hover:bg-sidebar-accent/50 transition-colors"
                onClick={() => setNotifOpen((v) => !v)}
                title="Notifications OPS"
              >
                <Bell className="h-4 w-4 text-muted-foreground" />
                {unreadCount > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-destructive text-[10px] font-bold text-white px-1">
                    {unreadCount > 99 ? "99+" : unreadCount}
                  </span>
                )}
              </button>
              {notifOpen && (
                <div className="absolute left-0 top-10 z-50 w-80 rounded-lg border bg-popover shadow-lg">
                  <div className="flex items-center justify-between px-3 py-2 border-b">
                    <span className="text-sm font-medium">Notifications</span>
                    {unreadCount > 0 && (
                      <button
                        type="button"
                        className="text-xs text-primary hover:underline"
                        onClick={markAllRead}
                      >
                        Tout marquer comme lu
                      </button>
                    )}
                  </div>
                  <div className="max-h-80 overflow-y-auto">
                    {notifications.length === 0 ? (
                      <div className="py-6 text-center text-xs text-muted-foreground">
                        Aucune notification
                      </div>
                    ) : (
                      notifications.slice(0, 20).map((n) => (
                        <button
                          type="button"
                          key={n.id}
                          className={cn(
                            "w-full text-left px-3 py-2 text-xs border-b last:border-0 hover:bg-muted/50 transition-colors",
                            !n.read && "bg-primary/5"
                          )}
                          onClick={() => {
                            if (n.link) navigate(n.link);
                            setNotifOpen(false);
                          }}
                        >
                          <div className="flex items-center gap-2">
                            <span
                              className={cn(
                                "inline-block h-2 w-2 rounded-full shrink-0",
                                n.type === "NEW_DISPUTE" && "bg-red-500",
                                n.type === "MESSAGE_FLAGGED" && "bg-orange-500",
                                n.type === "TRIAL_J3" && "bg-yellow-500",
                                n.type === "PENDING_ARTISAN" && "bg-blue-500",
                                n.type === "REFUND_CLAIM" && "bg-orange-500",
                              )}
                            />
                            <span className="font-medium truncate">
                              {n.type === "NEW_DISPUTE" && "Litige"}
                              {n.type === "MESSAGE_FLAGGED" && "Message suspect"}
                              {n.type === "TRIAL_J3" && "Trial J-3"}
                              {n.type === "PENDING_ARTISAN" && "Artisan en attente"}
                              {n.type === "REFUND_CLAIM" && "Remboursement"}
                            </span>
                            <span className="ml-auto text-muted-foreground shrink-0">
                              {new Date(n.timestamp).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}
                            </span>
                          </div>
                          {(n.companyName || n.name || n.reference) && (
                            <div className="text-muted-foreground mt-0.5 truncate">
                              {n.companyName || n.name || n.reference}
                            </div>
                          )}
                        </button>
                      ))
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Search */}
          <div className="p-4 border-b border-sidebar-border space-y-2">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Naviguer dans le menu..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
              />
            </div>
            <button
              type="button"
              onClick={() => navigate(searchQuery.trim().length >= 2 ? `/search?q=${encodeURIComponent(searchQuery.trim())}` : "/search")}
              className="w-full text-xs text-muted-foreground hover:text-foreground hover:bg-sidebar-accent/40 rounded px-2 py-1 text-left"
              title="Recherche globale (artisan, particulier, projet, entreprise)"
            >
              🔎 Recherche globale (Cmd+K)
            </button>
          </div>

          {/* Navigation — Grouped */}
          <nav className="flex-1 p-3 overflow-y-auto space-y-1">
            {filteredGroups.map((group) => {
              const isCollapsed = collapsedGroups[group.key] && !searchQuery.trim();
              const groupHasActiveItem = group.items.some(
                (item) =>
                  location.pathname === item.path ||
                  (item.path !== "/" && location.pathname.startsWith(item.path))
              );

              return (
                <div key={group.key}>
                  <button
                    onClick={() => toggleGroup(group.key)}
                    className={cn(
                      "flex items-center justify-between w-full rounded-md px-3 py-1.5 text-xs font-semibold uppercase tracking-wider transition-colors",
                      groupHasActiveItem
                        ? "text-sidebar-accent-foreground"
                        : "text-sidebar-foreground/50 hover:text-sidebar-foreground/70"
                    )}
                  >
                    {group.label}
                    {isCollapsed ? (
                      <ChevronRight className="h-3 w-3" />
                    ) : (
                      <ChevronDown className="h-3 w-3" />
                    )}
                  </button>
                  {!isCollapsed && (
                    <div className="space-y-0.5 mt-0.5">
                      {group.items.map((item) => {
                        const Icon = item.icon;
                        const isActive =
                          location.pathname === item.path ||
                          (item.path !== "/" && location.pathname.startsWith(item.path));
                        return (
                          <Link
                            key={item.path}
                            to={item.path}
                            className={cn(
                              "flex items-center justify-between rounded-lg px-3 py-1.5 text-sm font-medium transition-colors",
                              isActive
                                ? "bg-sidebar-accent text-sidebar-accent-foreground"
                                : "text-sidebar-foreground hover:bg-sidebar-accent/50"
                            )}
                          >
                            <div className="flex items-center gap-3">
                              <Icon className="h-4 w-4" />
                              {item.label}
                            </div>
                            {item.badge !== undefined && item.badge > 0 && (
                              <Badge variant="destructive" className="ml-auto h-5 min-w-5 px-1.5 text-xs">
                                {item.badge > 99 ? "99+" : item.badge}
                              </Badge>
                            )}
                          </Link>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </nav>

          {/* User section */}
          <div className="border-t border-sidebar-border p-4">
            <div className="mb-3 flex items-center gap-3">
              <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary/10 text-primary text-sm font-semibold">
                {user?.email?.[0]?.toUpperCase() || "A"}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium truncate">{user?.email}</div>
                <div className="text-xs text-muted-foreground">
                  {user?.staffRole === "SUPER_ADMIN" && "Super Admin"}
                  {user?.staffRole === "ADMIN_OPS" && "Admin OPS"}
                  {user?.staffRole === "ADMIN_SALES" && "Admin Commercial"}
                  {user?.staffRole === "ADMIN_SUPPORT" && "Observateur"}
                </div>
              </div>
            </div>
            <Button variant="ghost" className="w-full justify-start" onClick={handleLogout}>
              <LogOut className="h-4 w-4 mr-2" />
              Déconnexion
            </Button>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="ml-64">
        <main key={location.pathname} className="p-8 animate-slide-up">
          <RouteErrorBoundary>{children}</RouteErrorBoundary>
        </main>
      </div>

      <CommandPalette />
    </div>
  );
}

